import Vue from 'vue'
import gql from 'graphql-tag'
import { Component, Watch } from 'vue-property-decorator'
import { mixins } from 'vue-class-component'
import { State } from 'vuex-class'

import { Breadcrumb } from '@/interfaces/breadcrumbs'
import { Policy, PolicyStatus } from '@/modules/Policy/interfaces/policy'
import { ApolloResponse } from '@/interfaces/apollo'
import { showErrors } from '@/utils/snack'
import { tablePerPageOptions } from '@/utils/tablePerPageOptions'
import AppBreadcrumbs from '@/components/AppBreadcrumbs.vue'
import AppSearch from '@/components/AppSearch.vue'
import AppTextField from '@/components/AppTextField.vue'
import AppRefresh from '@/components/AppRefresh.vue'
import AppDialog from '@/components/AppDialog.vue'
import AppRadio from '@/components/AppRadio.vue'
import AppTextSearch from '@/components/AppTextSearch.vue'
import AppSelector from '@/components/AppSelector.vue'
import AppListSearchBtn from '@/components/AppListSearchBtn.vue'
import AppCheckbox from '@/components/AppCheckbox.vue'
import AppTableDate from '@/components/AppTableDate.vue'
import AppDatePicker from '@/components/AppDatePicker.vue'
import PolicyDialog from '@/modules/Policy/scripts/policy-dialog'
import { getPolicies, getAllGlobalSearchPolicies } from '@/modules/Policy/gql/policy.gql'
import { policyStatus, policyStatusClass, statusKeys, statusValues } from '@/modules/Policy/helpers/policy'
import ListPage from '@/mixins/list-page'
import SearchCarrier from '@/mixins/search-queries/search-carrier'
import SearchCarrierProduct from '@/mixins/search-queries/search-carrier-product'
import SearchProducer from '@/mixins/search-queries/search-producer'
import SearchGeneralAgent from '@/mixins/search-queries/search-ga'
import dollarFilter from '@/utils/dollarFilter'
import PolicyForm from '@/modules/Policy/components/PolicyForm.vue'
import AppFilterButton from '@/components/AppFilterButton.vue'
import AppSimpleTextField from '@/components/AppSimpleTextField.vue'
import AppSimpleSelector from '@/components/AppSimpleSelector.vue'
import AppSimpleTextSearch from '@/components/AppSimpleTextSearch.vue'
import AppSimpleDatePicker from '@/components/AppSimpleDatePicker.vue'
import { PageState } from '../../../store/modules/page'

@Component({
  components: {
    AppBreadcrumbs,
    AppSearch,
    AppTextField,
    AppRefresh,
    AppDialog,
    AppRadio,
    AppTextSearch,
    AppCheckbox,
    AppTableDate,
    AppSelector,
    AppListSearchBtn,
    AppDatePicker,
    PolicyForm,
    AppFilterButton,
    AppSimpleTextField,
    AppSimpleSelector,
    AppSimpleTextSearch,
    AppSimpleDatePicker
  }
})
export default class PoliciesList extends mixins(PolicyDialog, ListPage, SearchCarrier, SearchCarrierProduct, mixins(SearchProducer, SearchGeneralAgent)) {
  breadcrumbs: Breadcrumb[] = [
    {
      text: 'Insurance Cases'
    },
    {
      text: 'Policies'
    }
  ]
  searchVal = ''
  ifRelatedList = false

  policies: Policy[] = []

  orderBy: {
    [key: string]: string
  } = {
    id: 'ID',
    policyName: 'POLICY_NAME',
    effectiveDate: 'EFFECTIVE_DATE',
    expirationDate: 'EXPIRATION_DATE',
    bindDate: 'BIND_DATE',
    number: 'POLICY_NUMBER',
    status: 'POLICY_STATUS'
  }

  windowSize = {
    w: 0,
    h: 0,
    cW: 0
  }

  dollarFilter = dollarFilter

  tableColumn = [
    { field: 'number',
      title: 'Policy Number',
      slots: { default: ({ row }: any, h: any) => {
        return [
          h('span', row.policyNumber ? row.policyNumber : '-')
        ]
      }
      },
      sortable: true,
      resizable: true },
    { field: 'policyName',
      title: 'Policy Name',
      slots: { default: 'name_default' },
      resizable: true,
      sortable: true,
      width: 300
    },
    {
      field: 'status',
      title: 'Status',
      slots: { default: 'status_default' },
      sortable: true,
      resizable: true,
      width: 180
    },
    { title: 'Carrier',
      field: 'carrier',
      slots: { default: 'carrier_name_default' },
      resizable: true
    },
    { title: 'Insurance Product',
      field: 'carrierProduct',
      slots: { default: 'product_name_default' },
      resizable: true
    },
    { title: 'Premium',
      field: 'premium',
      slots: { default: 'premium_default' },
      resizable: true
    },
    { title: 'Source',
      field: 'source',
      slots: { default: ({ row }: any, h: any) => {
        return [
          h('span', row.source ? row.source.name : '-')
        ]
      }
      },
      resizable: true
    },
    { title: 'GA',
      field: 'generalAgent',
      slots: { default: ({ row }: any, h: any) => {
        return [
          h('span', row.generalAgent ? row.generalAgent.name : '-')
        ]
      }
      },
      resizable: true
    },
    { title: 'Bind Date',
      field: 'bindDate',
      slots: {
        default: 'bind_date'
      },
      resizable: true,
      sortable: true,
      width: 120
    },
    { title: 'Effective Date',
      field: 'effectiveDate',
      slots: {
        default: 'effective_default'
      },
      resizable: true,
      sortable: true,
      width: 140
    },
    { title: 'Expiration Date',
      field: 'expirationDate',
      slots: {
        default: 'expiration_default'
      },
      sortable: true,
      width: 160,
      resizable: true
    },
    { field: 'actions', title: 'Actions', slots: { default: 'action_default' }, fixed: 'right', width: 74 }
  ]

  tableOptions = {
    loading: false,
    tablePage: tablePerPageOptions(),
    sortBy: 'name',
    sortDesc: true
  }

  policyStatus = policyStatus
  policyStatusClass = policyStatusClass
  policyStatusOptions = statusValues
  PolicyStatus = PolicyStatus
  policyPaymentOptions = ['> 0', '= 0']

  tableLoading = false

  get isShowFilter () {
    return this.$route.name !== 'PoliciesListWithSearchval'
  }

  @State(state => state.page) page!: PageState

  async created () {
    const { search } = this.$route.params
    this.searchVal = search
    await this.restorePage(this.page[this.$route.name || ''])
  }

  async mounted () {
    this.onResize()
    await this.restorePage(this.page[this.$route.name || ''])
    this.getList()
  }

  getList () {
    if (this.$route.name === 'PoliciesListWithSearchval') {
      this.getQuotesFromDashboard()
    } else {
      this.getPolicies()
    }
  }

  onResize () {
    const container = this.$refs.container as Vue & { clientWidth: number }
    this.windowSize = { w: window.innerWidth, h: window.innerHeight, cW: container.clientWidth }
  }

  onCreateSuccess () {
    this.hidePolicyDialog()
    this.getList()
  }

  routeTo (name: string, params: any) {
    this.$router.push({
      name,
      params
    })
  }

  routeToDetail (item: any) {
    return {
      name: 'PolicyDetailDetail',
      params: {
        id: item.id
      }
    }
  }

  routeToCarrierDetail (item: any) {
    return {
      name: 'CarrierDetailDetail',
      params: {
        id: item.carrier.id
      }
    }
  }

  routeToCarrierProductDetail (item: any) {
    return {
      name: 'CarrierProductDetailDetail',
      params: {
        id: item.carrierProduct.id
      }
    }
  }

  async getPolicies () {
    const { sortBy, sortDesc, tablePage } = this.options
    const { currentPage, pageSize } = tablePage
    const orderBy = this.orderBy[sortBy] || 'UPDATED_AT'
    const filter: {
      [key: string]: any;
    } = {}
    if (this.filter.policyName) {
      filter.policyName = this.filter.policyName
    }
    if (this.filter.policyNumber) {
      filter.policyNumber = this.filter.policyNumber
    }
    if (this.filter.accountName) {
      filter.accountName = this.filter.accountName
    }
    if (this.filter.policyStatus !== null && !isNaN(this.filter.policyStatus) && this.filter.policyStatus !== -1) {
      filter.status = statusKeys[this.filter.policyStatus]
    }
    if (this.filter.bindStartDate) {
      filter.bindStartDate = this.filter.bindStartDate.toString()
    }
    if (this.filter.bindEndDate) {
      filter.bindEndDate = this.filter.bindEndDate.toString()
    }
    if (this.filter.effectiveStartDate) {
      filter.effectiveStartDate = this.filter.effectiveStartDate.toString()
    }
    if (this.filter.effectiveEndDate) {
      filter.effectiveEndDate = this.filter.effectiveEndDate.toString()
    }
    if (this.filter.expirationStartDate) {
      filter.expirationStartDate = this.filter.expirationStartDate.toString()
    }
    if (this.filter.expirationEndDate) {
      filter.expirationEndDate = this.filter.expirationEndDate.toString()
    }
    if (this.filter.caseId !== null && !isNaN(this.filter.caseId)) {
      filter.caseId = Number(this.filter.caseId)
    }
    if (this.filter.contactId !== null && !isNaN(this.filter.contactId)) {
      filter.contactId = Number(this.filter.contactId)
    }
    if (this.filter.accountId !== null && !isNaN(this.filter.accountId)) {
      filter.accountId = Number(this.filter.accountId)
    }
    if (this.filter.relatedcarrierProductId !== null && !isNaN(this.filter.relatedcarrierProductId)) {
      filter.carrierProductId = Number(this.filter.relatedcarrierProductId)
    }
    if (this.filter.producerId !== null && !isNaN(this.filter.producerId)) {
      filter.producerId = Number(this.filter.producerId)
    }
    if (this.filter.quoteId !== null && !isNaN(this.filter.quoteId)) {
      filter.quoteId = Number(this.filter.quoteId)
    }
    if (this.filter.isCaseRelated !== null && !isNaN(this.filter.isCaseRelated)) {
      filter.isCaseRelated = this.filter.isCaseRelated
    }
    if (this.filter.carrier && this.filter.carrier !== undefined) {
      filter.carrierId = Number(this.filter.carrier.id)
    }
    if (this.filter.carrierProduct && this.filter.carrierProduct.id !== undefined) {
      filter.carrierProductId = Number(this.filter.carrierProduct.id)
    }
    if (this.filter.source && this.filter.source.id !== undefined) {
      filter.sourceId = Number(this.filter.source.id)
    }
    if (this.filter.ga && this.filter.ga.id !== undefined) {
      filter.gaId = Number(this.filter.ga.id)
    }

    if (this.filter.hasPayment !== null && !isNaN(this.filter.hasPayment) && this.filter.hasPayment !== -1) {
      filter.hasPayment = this.filter.hasPayment === 0
    }
    this.tableLoading = true
    try {
      const result: ApolloResponse = await this.$apollo.query({
        query: getPolicies,
        variables: {
          filter,
          order: {
            orderBy: orderBy,
            asc: !sortDesc
          },
          paging: {
            offset: (currentPage - 1) * pageSize,
            limit: pageSize
          }
        }
      })
      if (!result.data) {
        showErrors(result.errors)
        return
      }
      this.policies = result.data.getPolicies.policies
      this.tableLoading = false
      this.options.tablePage.total = result.data.getPolicies.paging.totalCount
      this.storePage()
    } catch (e) {
      showErrors(e.graphQLErrors)
    }
  }

  async getQuotesFromDashboard () {
    const { sortBy, sortDesc, tablePage } = this.options
    const { currentPage, pageSize } = tablePage
    const orderBy = this.orderBy[sortBy] || 'UPDATED_AT'
    this.tableLoading = true
    try {
      const result: ApolloResponse = await this.$apollo.query({
        query: getAllGlobalSearchPolicies,
        variables: {
          query: this.$route.params.search,
          order: {
            orderBy: orderBy,
            asc: !sortDesc
          },
          paging: {
            offset: (currentPage - 1) * pageSize,
            limit: pageSize
          }
        }
      })
      if (!result.data) {
        showErrors(result.errors)
        return
      }
      this.policies = result.data.globalSearchPolicies.policies
      this.tableLoading = false
      this.options.tablePage.total = result.data.globalSearchPolicies.paging.totalCount
      this.storePage()
    } catch (e) {
      showErrors(e.graphQLErrors)
    }
  }

  async refreshList () {
    await this.clearPage()
    await this.restorePage(this.page[this.$route.name || ''])
    this.getList()
  }

  async deletePolicyConfirm () {
    await this.deletePolicyPost()
    this.getList()
  }

  sortTable (column: any) {
    this.options.sortBy = column.property
    this.options.sortDesc = column.order === 'desc'
    this.getList()
  }

  handlePageChange (pageData: any) {
    this.options.tablePage.currentPage = pageData.currentPage
    this.options.tablePage.pageSize = pageData.pageSize
    this.getList()
  }

  calculateExpiringDays (policy: Policy) {
    const { $moment } = this
    const { expirationDate } = policy

    if (!expirationDate) {
      return '-'
    }

    return $moment.unix(expirationDate).tz('America/New_York').diff($moment.tz('America/New_York'), 'days', false)
  }
}
